import { ng } from '@uirouter/angularjs/lib/angular';
import * as angular from 'angular';
import { FileItem, FileUploader } from 'angular-file-upload';
import { taOptions } from 'textangular';
import * as _ from 'underscore';
import { HttpService, IErrorResponse } from '../../../../app.common/modules/api/http.service';
import { WysiwygControllerBase } from '../../../../app.common/modules/common/controllers/wysiwygControllerBase';
import { PopupServiceCancel } from '../../../../app.common/modules/common/models/popupServiceCancel';
import { BlobService, IImageData } from '../../../../app.common/modules/common/services/blob.service';
import { HtmlService } from '../../../../app.common/modules/common/services/html.service';
import { NotificationService } from '../../../../app.common/modules/common/services/notification.service';
import { PopupService } from '../../../../app.common/modules/common/services/popup.service';
import { WysiwygService } from '../../../../app.common/modules/common/services/wysiwyg.service';
import { CommonCampaignsService } from '../../../../app.retailer.common/modules/services/commonCampaigns.service';
import { RetailerService } from '../../api/retailer.service';
import { Campaign } from '../../core';
import { IRetailerRootScope } from '../../core/models';
import { INfcStickersErrors } from '../../products/models';
import { IStoresListItem } from '../../products/productBase.controller';
import { IAudienceOffer, ICampaign, ICampaignImage, ICampaignVideo, ICampaignVideoManager, IGroupAudience, IKeyword, IOfferAudience, IPlaceAudience, IPlacesGroupAudience } from '../models';
import { CampaignsService } from '../services/campaigns.service';

interface ISize {
    w: number;
    h: number;
}

export interface ICroppedImage extends ICampaignImage {
    src: string;
    selector: {
        enabled: boolean;
        size: ISize;
        imageSize?: ISize;
        x1?: number;
        clear?: () => void;
    };
    minSize: ISize;
    maxSize: ISize;
    maxWidth: number;
    scale: number;
    aspectRatio: number;
    wasChanged: boolean;
    isBadBrowser: boolean;
    loading: boolean;
    sizeCropped?: any;
}

interface ICampaignEntityState extends ICampaignType {
    isDisabled: () => boolean;
}

interface ICampaignType {
    value: number;
    title: string;
}

export abstract class CreateEditCampaignBase extends WysiwygControllerBase {
    // region properties
    public ID: number = null;
    public campaign: ICampaign = null;
    public stores: IStoresListItem[];
    public canEditStickers: boolean;
    public stickersErrors: INfcStickersErrors[];

    public campaignType = this.$rootScope.enums.CampaignType;

    public firstImgLoad = false;
    public imageCropped: ICroppedImage = null;
    public videoManager: ICampaignVideoManager = null;
    public videos: ICampaignVideo[] = [];
    public video: ICampaignVideo = null;
    public campaignForm: ng.IFormController;
    public waiting = true;

    public dateOptions = {
        formatYear: 'yyyy',
        startingDay: 0
    };

    public accordionOptions = {
        setupGroupError: false,
        setupGroupOpen: true,
        audienceGroupError: false,
        audienceGroupOpen: false,
        designGroupError: false,
        designGroupOpen: false,
        scheduleGroupError: false,
        scheduleGroupOpen: false,
        scheduleGroupShow: true,
        summaryGroupOpen: true,
        keywordsGroupError: false,
        keywordsGroupOpen: false,
    };

    public campaignPushNotificationShow = false;

    public campaignStatus = this.commonCampaignsService.getCampaignStatus();
    public videoStatus = this.commonCampaignsService.getVideoStatus();
    public customScroll = this.campaignsService.customScrollView;
    public campaignEntityStates: ICampaignEntityState[];
    public campaignTypes: ICampaignType[];

    public uploader: FileUploader = null;

    public audienceService = {
        changeModel: false, // need for udate audience view
        model: null as IAudienceOffer,
        enabledChange: (group: IGroupAudience) => {
            _.forEach(group.items, (item) => {
                item.selected = !group.enabled;
            });
            group.selected = !group.enabled;
            this.audienceService.update();
        },
        selectedChange: (group: IGroupAudience) => {
            _.forEach(group.items, (item) => {
                item.selected = group.selected;
            });
            this.audienceService.update();
        },
        enabledPlaceChange: () => {
            const places = this.audienceService.model.places;
            _.forEach(places.items as IPlaceAudience[], (item) => {
                item.selected = item.isArchived ? false : !places.enabled;
            });
            places.selected = !places.enabled;
            this.audienceService.update();
        },
        selectedPlaceChange: (group: IPlacesGroupAudience) => {
            _.forEach(group.items as IPlaceAudience[], (item) => {
                item.selected = item.isArchived ? false : group.selected;
            });
            this.audienceService.update();
        },
        changeSelectedPlace: (option: IPlaceAudience, group: IPlacesGroupAudience) => {
            group.selected = _.all(group.items, (item) => item.selected);
            const selected = _.where(group.items, { selected: true }) as IPlaceAudience[];
            this.audienceService.update();
        },
        // need for udate audience view
        update: () => {
            this.audienceService.changeModel = !this.audienceService.changeModel;
        },
        getOfferAudience: () => {
            const model = this.audienceService.model;
            const audience = {
                ages: model.ages.enabled ? _.pluck(_.where(model.ages.items, { selected: true }), 'id') : [],
                categoryIDs: model.categories.enabled ? _.pluck(_.where(model.categories.items, { selected: true }), 'id') : [],
                interests: model.interests.enabled ? _.pluck(_.where(model.interests.items, { selected: true }), 'id') : [],
                genders: model.genders.enabled ? _.pluck(_.where(model.genders.items, { selected: true }), 'id') : [],
                placeIDs: model.places.enabled ? _.pluck(_.where(model.places.items, { selected: true }), 'id') : [],
            } as IOfferAudience;
            return audience;
        },
        validate: {
            incompleteGroup: () => {
                const model = this.audienceService.model;
                function incompleteGroup(gr: IGroupAudience) {
                    return gr.enabled ? _.any(gr.items, (item) => item.selected) : true;
                }
                return incompleteGroup(model.ages) && incompleteGroup(model.categories) && incompleteGroup(model.genders) && incompleteGroup(model.interests) && incompleteGroup(model.places);
            },
            audiencePlaceGroup: () => {
                const places = this.audienceService.model.places as IPlacesGroupAudience;
                return places.enabled ? !_.any(places.items as IPlaceAudience[], (item) => item.selected && item.isArchived) : true;
            },
            storeCategory: () => {
                const model = this.audienceService.model;
                if (!model.categories.enabled || !model.places.enabled) {
                    return true;
                }
                const selectedCategoryIDs: number[] = _.pluck(_.where(model.categories.items, { selected: true }), 'id');
                if (_.isEmpty(selectedCategoryIDs)) {
                    return true;
                }
                const selectedPlaces = _.where(model.places.items as IPlaceAudience[], { selected: true });

                const allowedCategoryIDs = _.uniq(_.reduce(_.pluck(selectedPlaces, 'categoryIDs'),
                    function(a: any[], b: any[]) {
                        return a.concat(b);
                    }, []));

                const badStores = _.filter(selectedPlaces, (place) => {
                    return _.all(place.categoryIDs, (allowedID) => {
                        return !_.contains(selectedCategoryIDs, allowedID);
                    });
                });

                if (!_.isEmpty(badStores)) {
                    this.showBadStores(badStores);
                    return false;
                }
                return true;
            }
        },
    };

    constructor(
        protected $rootScope: IRetailerRootScope,
        protected $scope: ng.IScope,
        protected $timeout: ng.ITimeoutService,
        protected commonCampaignsService: CommonCampaignsService,
        protected campaignsService: CampaignsService,
        protected popupService: PopupService,
        protected notificationService: NotificationService,
        protected htmlService: HtmlService,
        protected taOptions: taOptions,
        protected wysiwygService: WysiwygService,
        protected $translate: angular.translate.ITranslateService,
        protected retailerService: RetailerService,
        protected fileUploaderFactory: any, // FileUploaderFactory
        protected $q: ng.IQService,
        protected http: HttpService,
        protected blobService: BlobService
    ) {
        super($scope, taOptions, wysiwygService);
        this.ID = parseInt($rootScope.$stateParams.id, 10);

        this.imageCropped = {
            imageID: null,
            imageUrl: null,
            fileName: null,
            src: '',
            selector: {
                enabled: true,
                size: null,
                clear: () => {
                    const selector = this.imageCropped.selector;
                    selector.enabled = true;
                    selector.size = null;
                }
            },
            minSize: { w: 640, h: 320 },
            maxSize: { w: 1200, h: 600 },
            croppedImage: null,
            maxWidth: 600,
            scale: 1,
            aspectRatio: 2,
            wasChanged: false,
            isBadBrowser: window.navigator.appName.toLowerCase().indexOf('internet explorer')
                !== -1 && window.navigator.appVersion.toLowerCase().indexOf('MSIE 9') !== -1,
            loading: false,
            isGif: false
        };

        this.videoManager = {
            uploading: false,
            uploadingError: false,
            uploadingProgress: 0,
            processing: false,
            processingError: false,
            processed: false,
            maxQueueSize: 10,
            maxFileSizeMb: 500,
            queueSizeFilterName: 'queueSizeFilter',
            fileSizeFilterName: 'fileSizeFilter',
            error: {
                fileTypeError: false,
                fileSizeError: false,
                customError: null,
                clear: () => {
                    const v = this.videoManager;
                    v.error.fileTypeError = false;
                    v.error.fileSizeError = false;
                    v.error.customError = null;
                    v.uploading = false;
                    v.uploadingError = false;
                    v.uploadingProgress = 0;
                    v.processing = false;
                    v.processingError = false;
                    v.processed = false;
                }
            },
            clear: () => {
                const v = this.video;
                if (!v) {
                    return;
                }

                v.ID = null;
                v.VideoUrl = null;
                v.TeaserUrl = null;
                v.ThumbnailUrl = null;
                v.Width = null;
                v.Height = null;
                v.videoID = null;
                v.campaignStatus = null;
            }
        };

        this.video = {
            ID: null,
            VideoUrl: null,
            TeaserUrl: null,
            ThumbnailUrl: null,
            Width: null,
            Height: null,
            videoID: null,
            campaignStatus: null
        };

        this.campaignEntityStates = [
            {
                value: this.$rootScope.enums.CampaignEntityStatus.Active,
                title: this.$translate.instant('active'),
                isDisabled: () => {
                    return !this.isValidMandatoryFields();
                }
            },
            {
                value: this.$rootScope.enums.CampaignEntityStatus.QualityControl,
                title: this.$translate.instant('quality_control'),
                isDisabled: () => {
                    return false;
                }
            },
            {
                value: this.$rootScope.enums.CampaignEntityStatus.AdSubmission,
                title: this.$translate.instant('ad_submission'),
                isDisabled: () => {
                    return false;
                }
            },
            {
                value: this.$rootScope.enums.CampaignEntityStatus.Draft,
                title: this.$translate.instant('draft'),
                isDisabled: () => {
                    return false;
                }
            }
        ];

        this.campaignTypes = [
            {
                value: this.$rootScope.enums.CampaignType.Promotion,
                title: this.$translate.instant('promotion')
            }, {
                value: this.$rootScope.enums.CampaignType.Offer,
                title: this.$translate.instant('offer')
            }, {
                value: this.$rootScope.enums.CampaignType.FlashSale,
                title: this.$translate.instant('flash_sale')
            }, {
                value: this.$rootScope.enums.CampaignType.Event,
                title: this.$translate.instant('event')
            }, {
                value: this.$rootScope.enums.CampaignType.Voucher,
                title: this.$translate.instant('voucher')
            }
        ];

        this.initUploader();
    }
    // region Events
    public createCampaign = async () => {
        return this.campaignsService.createCampaign(this.campaign, this.imageCropped, this.video);
    }

    public updateCampaign = async () => {
        return this.campaignsService.updateCampaign(this.ID, this.campaign, this.imageCropped, this.video);
    }

    public archiveCampaign = async () => {
        try {
            await this.popupService.confirm({
                title: this.$translate.instant('archive_campaign'),
                content: `<p>${this.$translate.instant('archive_campaing_confirm')}</p>`,
                cancelClass: 'btn-success',
                okTxt: this.$translate.instant('no'),
                cancelTxt: this.$translate.instant('yes'),
                invertAction: true
            });
            await this.campaignsService.deleteCampaign(this.ID);
            this.notificationService.showSuccessMessage(this.$translate.instant('campaing_has_been_archived'));
            this.$timeout(1500);
            this.$scope.$emit('campaignsChanged');
            this.$rootScope.$state.go('app.marketing-campaigns.overview');
        } catch (error) {
            if (!PopupServiceCancel.isCancelOrBackdropClickInstance(error)) {
                this.notificationService.showErrorMessage(error);
            }
        }
    }

    public pageBack = () => {
        if (this.ID) {
            if (this.campaign.type === this.campaignType.Advert) {
                this.$rootScope.$state.go('app.advertising.campaign.view', { id: this.ID });
            } else {
                this.$rootScope.$state.go('app.marketing-campaigns.view', { id: this.ID });
            }
        } else {
            if (this.campaign.type === this.campaignType.Advert) {
                this.$rootScope.$state.go('app.advertising.overview');
            } else {
                this.$rootScope.$state.go('app.marketing-campaigns.overview');
            }
        }
    }

    public needHelp = async () => {
        try {
            await this.campaignsService.needHelp();
        } catch (error) {
            return;
        }
    }

    protected initWatch = () => {
        this.$timeout(() => {
            angular.element(document.querySelector('#fileInputCropped')).on('change', this.handleFileSelect);
            this.$timeout(() => {
                angular.element(document.querySelector('#campaignName')).focus();
            }, 500);
        }, 500);

        if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
            this.$scope.$watch('vm.campaign.additionalInfo', (value: string, old: string) => {
                // disable img autoresize in firefox in angularTextEditor
                this.$timeout(() => {
                    const imgs = document.getElementsByTagName('IMG');
                    for (let i = 0; i < imgs.length; ++i) {
                        (imgs[i] as any).contentEditable = false;
                    }
                });
            });
        }

        this.$scope.$on('$destroy', () => {
            $(window).off('resize', this.setHeightPanel);
        });
    }

    protected setHeightPanel = () => {
        $('.panel-quick-camp-twoCol > form > .panel-body').css('min-height', ($(window).height() - $('header').height() - 219) + 'px'); // for narrow column
        $('.panel-quick-camp > form > .panel-body').css('min-height', ($(window).height() - $('header').height() - 202) + 'px'); // for wide column
        $('.panel-campaign-choice .tab-content').height($(window).height() - $('header').height() - 303);
    }
    // endregion

    // region StatusModel
    public isCompletedCampaignModel = () => {
        return this.campaign.schedule.endDate && this.commonCampaignsService.getDateTime(this.campaign.schedule.endDate.toString(), '00:00') < this.commonCampaignsService.getDateTime(null, '00:00');
    }

    public isScheduledCampaignModel = () => {
        return this.campaign.schedule.startDate && this.commonCampaignsService.getDateTime(this.campaign.schedule.startDate.toString(), '00:00') > this.commonCampaignsService.getDateTime(null, '00:00');
    }

    public isErrorDesignCampaignModel = () => {
        return !this.campaign.image && !this.imageCropped.croppedImage && !this.video.videoID;
    }
    // endregion

    // region Video

    public setVideo = async (videoID: number) => {
        try {
            const videoMetadata = await this.campaignsService.getCampaignVideoMetadata(videoID);
            this.video.campaignStatus = this.campaignStatus.VideoProcessing;
            this.video.ID = videoID.toString();
            this.video.videoID = videoID;
            this.video.VideoUrl = videoMetadata.VideoUrl || this.retailerService.getCampaignVideoContentUrl(videoID);
            this.video.ThumbnailUrl = this.retailerService.getCampaignThumbnailImageUrl(videoID);
            this.video.Width = videoMetadata.Width;
            this.video.Height = videoMetadata.Height;
            this.videos = [];
            await this.$timeout(0);
            this.videos.push(this.video);
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
    }

    public clearVideo = () => {
        this.videos = [];
        this.videoManager.clear();
    }

    // region CropImage
    public fileInputCroppedClick = (event: Event) => {
        angular.element('#fileInputCropped').click();
    }

    public getCropImageDivWidth = () => {
        return $('.imgcrop-area').width();
    }

    public onCropped = (cropped: string) => {
        if (this.campaign.image && !_.isEmpty(cropped)) {
            this.campaignsService.setInitialCampaignImageData(this.campaign.image.imageID, cropped);
        }
        this.imageCropped.croppedImage = cropped ? cropped : null;
        if (this.imageCropped.selector.imageSize) {
            this.validateCampaignImageSize();
        }
        if (this.campaignForm) {
            this.campaignForm.$setDirty();
        }
    }

    public clearCropped = () => {
        this.imageCropped.croppedImage = null;
        this.imageCropped.sizeCropped = null;
        this.imageCropped.selector.clear();
    }

    public imageOnload = () => {
        if (this.firstImgLoad) {
            this.campaignForm.$setPristine();
            this.firstImgLoad = false;
        }
    }

    public onLoadImgCrop = async () => {
        await this.$timeout(0);
        this.imageCropped.loading = false;
    }
    // endregion
    // region  Rule/Content
    public updateCampaignModel = () => {
        if (this.campaignForm) {
            this.campaignForm.$setDirty();
        }
    }

    public copyWysiwygContent = async (obj: {}, key: string) => {
        if (this.instanceWysiwygService) {
            const copy = await this.instanceWysiwygService.copyHtmlContent(obj[key]);
            obj[key] = copy;
        }
    }

    public pasteHandler = (html: string) => {
        return this.htmlService.filterWysiwygContent(html);
    }
    // endregion
    // region keywords
    public addKeyword() {
        this.campaign.keywords.push({
            id: null,
            keyword: '',
            error: null
        });
    }

    public deleteKeyword(keyword: IKeyword) {
        this.campaign.keywords = _.reject(this.campaign.keywords, (item) => {
            return item === keyword;
        });
    }

    public updateKeyword() {
        const temp = angular.copy(this.campaign.keywords);
        this.campaign.keywords = temp;
    }
    // endregion
    // region Validate
    public validateCampaignKeyword = () => {
        this.campaignForm.keywords.$setValidity('required', true);
        this.campaignForm.keywords.$setValidity('maxlength', true);
        this.campaignForm.keywords.$setValidity('unique', true);
        _.forEach(this.campaign.keywords, (item) => {
            item.keyword = item.keyword ? item.keyword.trim() : '';
            item.error = null;
            if (_.isEmpty(item.keyword)) {
                item.error = this.$translate.instant('keyword_required_field');
                this.campaignForm.keywords.$setValidity('required', false);
            } else if (_.size(item.keyword) < 3 || _.size(item.keyword) > 64) {
                item.error = this.$translate.instant('keyword_length_error');
                this.campaignForm.keywords.$setValidity('maxlength', false);
            } else if (_.size(_.filter(this.campaign.keywords, (temp) => {
                return temp.keyword.trim().toLocaleLowerCase() === item.keyword.toLowerCase();
            })) > 1) {
                item.error = this.$translate.instant('keyword_unique_error');
                this.campaignForm.keywords.$setValidity('unique', false);
            }
        });
    }

    public validateDate = () => {
        this.campaignForm.campaignStartDate.$setValidity('date', true);
        this.campaignForm.campaignEndDate.$setValidity('date', true);
        this.campaignForm.campaignAlertDate.$setValidity('date', true);

        const schedule = this.campaign.schedule;

        let startDateIsValid = !schedule.hasStartDate || (schedule.hasStartDate && (schedule.startDate !== undefined));
        startDateIsValid = startDateIsValid ? true : false;

        let endDateIsValid = !schedule.hasEndDate
            || schedule.hasEndDate
            && schedule.endDate
            && this.getDateIsValid(schedule.endDate);
        endDateIsValid = endDateIsValid ? true : false;

        let alertDateIsValid = !schedule.hasAlertDate
            || schedule.hasAlertDate
            && schedule.alertDate
            && this.getDateIsValid(schedule.alertDate);
        alertDateIsValid = alertDateIsValid ? true : false;

        const keyInvalidDate = 'invalidDate';
        const keyRequired = 'required';

        this.campaignForm.campaignAlertDate.$setValidity(keyRequired, true);
        this.campaignForm.campaignStartDate.$setValidity(keyInvalidDate, startDateIsValid);
        this.campaignForm.campaignEndDate.$setValidity(keyInvalidDate, endDateIsValid);
        this.campaignForm.campaignAlertDate.$setValidity(keyInvalidDate, alertDateIsValid);

        if (this.campaign.entityStatus === Campaign.EntityStatusEnum.NUMBER_4 && !schedule.hasEndDate && !schedule.hasAlertDate && (!this.campaign.isExclusiveOffer && this.campaign.type !== this.campaignType.SpecialEvent)) {
            this.campaignForm.campaignAlertDate.$setValidity(keyRequired, false);
        }

        if (schedule.hasStartDate && startDateIsValid && schedule.hasEndDate && endDateIsValid) {
            const startDate = this.commonCampaignsService.getDateTime(schedule.startDate.toString(), '00:00');
            const endDate = this.commonCampaignsService.getDateTime(schedule.endDate.toString(), '00:00');
            const rangeDateIsValid = startDate <= endDate;

            this.campaignForm.campaignStartDate.$setValidity(keyInvalidDate, rangeDateIsValid);
            this.campaignForm.campaignEndDate.$setValidity(keyInvalidDate, rangeDateIsValid);
        }
    }

    public validateWeeksday = () => {
        const schedule = this.campaign.schedule;
        if (!schedule.hasDaily && schedule.daysOfWeekInt === 0) {
            this.campaignForm.datesHasDaily.$setValidity('invalidDaysOfWeek', false);
        } else {
            this.campaignForm.datesHasDaily.$setValidity('invalidDaysOfWeek', true);
        }
    }

    public validateCampaignName = () => {
        this.campaignForm.campaignName.$setValidity('required', true);
        this.campaignForm.campaignName.$setValidity('maxlength', true);

        if (!this.campaign.name || this.campaign.name.trim().length === 0) {
            this.campaignForm.campaignName.$setValidity('required', false);
            return;
        } else if (!this.campaign.name || this.campaign.name.trim().length > 100) {
            this.campaignForm.campaignName.$setValidity('maxlength', false);
            return;
        }

        this.campaignForm.campaignPushNotification.$setValidity('required', true);
        this.campaignForm.campaignPushNotification.$setValidity('maxlength', true);

        if (this.campaignPushNotificationShow && (!this.campaign.pushNotification || this.campaign.pushNotification.trim().length === 0)) {
            this.campaignForm.campaignPushNotification.$setValidity('required', false);
            return;
        } else if (this.campaignPushNotificationShow && (!this.campaign.pushNotification || this.campaign.pushNotification.trim().length > 100)) {
            this.campaignForm.campaignPushNotification.$setValidity('maxlength', false);
            return;
        }
    }

    public validateCampaignImageSize = () => {
        if (!this.campaignForm || !this.campaignForm || !this.campaignForm.useTemplate) { // TODO ???
            return;
        }

        this.campaignForm.useTemplate.$setValidity('imageRequired', true);
        this.campaignForm.useTemplate.$setValidity('imageMinAreaSize', true);
        this.campaignForm.useTemplate.$setValidity('imageMaxRatio', true);
        this.campaignForm.useTemplate.$setValidity('imageMinRatio', true);

        if (this.campaign.campaignContentType === this.$rootScope.enums.CampaignContentType.Image &&
            this.imageCropped.croppedImage != null &&
            this.imageCropped.selector.size != null) {
            const w = this.imageCropped.selector.size.w ? this.imageCropped.selector.size.w : this.imageCropped.selector.imageSize.w;
            const h = this.imageCropped.selector.size.h ? this.imageCropped.selector.size.h : this.imageCropped.selector.imageSize.h;
            // var validMinSize = $scope.imageCropped.selector.size.w >= $scope.imageCropped.minSize.w;
            this.campaignForm.useTemplate.$setValidity('imageMinAreaSize', w >= this.imageCropped.minSize.w && h >= this.imageCropped.minSize.h);

            const ratio = w === 0 ? 100 : h / w;
            this.campaignForm.useTemplate.$setValidity('imageMaxRatio', ratio <= 1.25);
            this.campaignForm.useTemplate.$setValidity('imageMinRatio', ratio >= 0.5);
        } else {
            if (this.campaign.campaignContentType === this.$rootScope.enums.CampaignContentType.Image && this.imageCropped.croppedImage == null && this.imageCropped.src) {
                const validSelectorSize = angular.isDefined(this.imageCropped.selector.x1);
                this.campaignForm.useTemplate.$setValidity('imageMinAreaSize', validSelectorSize);
                if (!validSelectorSize && this.imageCropped.selector.size) {
                    this.imageCropped.selector.size.w = null;
                }
            }
        }
    }

    public validateCampaignImage = () => {
        this.validateCampaignImageSize();
        if (this.campaign.campaignContentType === this.$rootScope.enums.CampaignContentType.Image && this.imageCropped.croppedImage === null) {
            this.campaignForm.useTemplate.$setValidity('imageRequired', false);
        } else {
            this.campaignForm.useTemplate.$setValidity('imageRequired', true);
        }
    }

    public isValidCampaignAudience = () => {

        let isValid = this.audienceService.validate.incompleteGroup();
        this.campaignForm.rules.$setValidity('incompleteGroup', isValid);

        isValid = this.audienceService.validate.audiencePlaceGroup();
        this.campaignForm.rules.$setValidity('audienceStores', isValid);

        isValid = this.audienceService.validate.storeCategory();
        this.campaignForm.rules.$setValidity('audienceStoreCampaigns', isValid);

        return isValid;
    }

    public isValidMandatoryFields = () => {
        return true;
    }
    // endregion

    protected setImageCropped(imgData: IImageData) {
        this.imageCropped.croppedImage = imgData.dataUri;
        this.imageCropped.src = imgData.dataUri;
        const size: ISize = {
            h: imgData.imageSize.height,
            w: imgData.imageSize.width
        };
        this.imageCropped.selector.size = size;
        this.imageCropped.selector.imageSize = size;
        this.onCropped(this.imageCropped.src);
    }

    private getDateIsValid(date: Date): boolean {
        return this.commonCampaignsService.getDateTime(date.toString(), '00:00') > this.commonCampaignsService.getDateTime(null, '00:00');
    }

    private getFileType = async (file: File) => {
        const deferred = this.$q.defer<string>();

        const blob = file.slice(0, 4 + 1);
        const fileReader = new FileReader();
        fileReader.onloadend = (e: any) => {
            const arr = (new Uint8Array(e.target.result)).subarray(0, 4);
            let header = '';
            for (let i = 0; i < arr.length; i++) {
                header += arr[i].toString(16);
            }

            let type = 'video';
            switch (header) {
                case '89504e47':
                    type = 'image/png';
                    break;
                case '47494638':
                    type = 'image/gif';
                    break;
                case 'ffd8ffe0':
                case 'ffd8ffe1':
                case 'ffd8ffe2':
                case 'ffd8ffe3':
                case 'ffd8ffdb':
                case 'ffd8ffe8':
                    type = 'image/jpeg';
                    break;
                default:
                    type = 'video';
                    break;
            }

            deferred.resolve(type);
        };

        fileReader.onerror = function(e: Event) {
            deferred.reject(e);
        };

        fileReader.readAsArrayBuffer(blob);

        return deferred.promise;
    }

    private checkVideoFileType = (file: File) => {
        const ext = file.name.split('.').pop();
        if (ext === file.name) {
            return false;
        }

        const exts = ['mp4', 'avi', 'wmv', 'mov', 'mpg', 'mkv', 'flv', '3gp', 'webm'];
        return (exts.indexOf(ext.toLowerCase()) > -1);
    }

    // region private
    private handleFileSelect = async (event: Event) => {
        await this.$timeout(0);
        const file: File = (event.currentTarget as HTMLInputElement).files[0];
        if (!file) {
            return;
        }

        const fileType = await this.getFileType(file);

        if (fileType === 'video') {
            this.campaign.campaignContentType = this.$rootScope.enums.CampaignContentType.Video;

            if (this.campaignForm) {
                this.campaignForm.$setDirty();
            }

            const success = this.checkVideoFileType(file);
            if (success) {
                this.videoManager.error.fileTypeError = false;
                this.uploader.clearQueue();
                this.uploader.addToQueue(file, null, null);
            } else {
                this.videoManager.error.fileTypeError = true;
            }
        } else {

            this.getCropImageDivWidth();
            this.campaignsService.resizeFontWithTimeOut();
            this.campaign.campaignContentType = (fileType === 'image/gif') ? this.$rootScope.enums.CampaignContentType.Gif : this.$rootScope.enums.CampaignContentType.Image;

            this.imageCropped.src = null;
            this.imageCropped.isGif = this.campaign.campaignContentType === this.$rootScope.enums.CampaignContentType.Gif;
            this.campaignForm.useTemplate.$setValidity('imageMinSize', true);
            this.campaignForm.useTemplate.$setValidity('fileType', true);

            if (_.isUndefined(file.size) || file.size > 5 * 1024 * 1024) {
                this.campaignForm.useTemplate.$setValidity('fileSize', false);
                return;
            }

            this.campaignForm.useTemplate.$setValidity('fileSize', true);

            this.imageCropped.loading = true;
            const reader = new FileReader();

            reader.onload = async (evtreader) => {
                const image = new Image();
                const target = evtreader.target as FileReader;
                image.onload = async () => {
                    await this.$timeout(0);
                    this.campaignForm.useTemplate.$setValidity('fileType', true);
                    if (image.width < this.imageCropped.minSize.w || image.height < this.imageCropped.minSize.h) {
                        this.imageCropped.src = null;
                        this.campaignForm.useTemplate.$setValidity('imageMinSize', false);
                        this.imageCropped.loading = false;
                    } else {
                        this.imageCropped.src = target.result as string;
                        if (this.imageCropped.isGif) {
                            const img: IImageData = {
                                dataUri: this.imageCropped.src,
                                imageSize: {
                                    height: image.height,
                                    width: image.width
                                },
                                size: file.size,
                                error: null,
                            };
                            this.setImageCropped(img);
                            this.imageCropped.loading = false;
                        }
                        this.campaignForm.useTemplate.$setValidity('imageMinSize', true);
                        this.clearVideo();
                    }
                };
                image.onerror = async () => {
                    await this.$timeout(0);
                    this.imageCropped.loading = false;
                    this.campaignForm.useTemplate.$setValidity('fileType', false);

                };
                image.src = target.result as string;
            };

            if (file) {
                reader.readAsDataURL(file);
                this.imageCropped.fileName = file.name;
            }
        }
    }

    private async showBadStores(badStores: IPlaceAudience[]) {
        let names = _.pluck(badStores, 'title');
        let message = '';
        if (_.isEmpty(names)) {
            return;
        } else if (_.size(names) === 1) {
            message = this.$translate.instant('campaign_wont_run_in_a_bad_store', { name: `<b>${names[0]}</b>` });
        } else if (_.size(names) > 1) {
            names = _.sortBy(names);
            const initial = _.initial(names).join(' ,');
            message = this.$translate.instant('campaign_wont_run_in_bad_stores', { initial: `<b>${initial}`, last: `${_.last(names)}</b>` });
        } else {
            return;
        }
        try {
            await this.popupService.alert({
                title: this.$translate.instant('cant_save_campaign'),
                content: message
            });

            $('html, body').animate({ scrollTop: 0 }, 1);

        } catch (error) {
            $('html, body').animate({ scrollTop: 0 }, 1);
        }
    }
    // endregion

    private initUploader() {
        const i = this;

        this.uploader = new this.fileUploaderFactory({
            headers: this.campaignsService.getAuthorizationBearer(),
            url: this.campaignsService.getUploadVideoUrl(),
            autoUpload: true
        });

        // filters
        this.uploader.filters.push({
            name: i.videoManager.queueSizeFilterName,
            fn(item: any /* {File|FileLikeObject} */, options: any) {
                return this.queue.length < i.videoManager.maxQueueSize;
            }
        });
        this.uploader.filters.push({
            name: i.videoManager.fileSizeFilterName,
            fn(item: any /* {File|FileLikeObject} */, options: any) {
                return item.size / 1024 / 1024 < i.videoManager.maxFileSizeMb;
            }
        });
        // events
        this.uploader.onBeforeUploadItem = (fileItem: FileItem) => {
            // clear all queue except selected file
            this.uploader.queue = _.filter(this.uploader.queue, function(item: any) {
                return fileItem.file.name === item.file.name;
            });
            this.videoManager.error.clear();
            this.videoManager.uploading = true;
        };

        this.uploader.onCancelItem = async (fileItem: any, response: any, status: any, headers: any) => {
            // if delete viewed file
            if (fileItem.serverSideID) {
                try {
                    await this.campaignsService.deleteCampaignVideo(fileItem.serverSideID);
                } catch (error) {
                    this.notificationService.showErrorMessage(error);
                }
            }

            if (fileItem.serverSideID === this.video.videoID) {
                this.clearVideo();
            }

            this.videoManager.uploading = false;
            this.uploader.clearQueue();
        };

        this.uploader.onSuccessItem = async (fileItem: any, response: any, status: any, headers: any) => {
            fileItem.serverSideID = response;
            fileItem.errorMessage = null;

            this.videoManager.uploading = false;
            this.videoManager.processing = true;
            this.setVideo(response);
            this.clearCropped();
        };

        this.uploader.onProgressItem = (fileItem: any, progress: number) => {
            this.videoManager.uploadingProgress = progress;
        };

        this.uploader.onProgressAll = (progress: number) => {
            this.waiting = progress !== 100;
            this.videoManager.uploadingProgress = progress;
        };

        this.uploader.onErrorItem = (fileItem: any, response: any, status: number, headers: any) => {
            if (_.isNumber(status) && status > 0) {
                const errorResponse: ng.IHttpResponse<IErrorResponse> = {
                    data: response,
                    status,
                    headers,
                    config: null,
                    statusText: null,
                    xhrStatus: 'error'
                };
                const message = this.http.decodeError(errorResponse) || response.ExceptionMessage;
                this.videoManager.error.customError = message;
            } else {
                this.videoManager.error.customError = 'Upload failed';
            }
            this.videoManager.uploadingError = true;
            this.videoManager.uploading = false;
        };

        this.uploader.onAfterAddingAll = async (addedItems: any[]) => {
            this.uploader.queue = _.filter(this.uploader.queue, function(item: any) {
                return !item.excludeFromQueue;
            });

            if (this.uploader.queue.length === 0) {
                this.videoManager.uploading = false;
            }
        };

        this.uploader.onWhenAddingFileFailed = (file: any /* {File|FileLikeObject} */, filter: any, options: any) => {
            file.excludeFromQueue = true;
            if (filter.name === this.videoManager.fileSizeFilterName) {
                this.videoManager.error.fileSizeError = true;
                this.uploader.queue.push(file);
            }
        };
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/campaigns/controllers/createEditCampaignBase.controller.ts